/**
 * KAS-1 / KAS-2 — the assignment-or-submission status card, and the talent-portal deflection.
 *
 * **The narrowing IS the contract** [KAS-1 § Implementation]. No pay, rate or payday appears in this shape
 * at all — not hidden in the UI, not present-but-unrendered in the payload. A field that is not in this
 * interface cannot be leaked later by a change to a component, which is a stronger guarantee than a rule
 * somebody has to remember while editing JSX.
 */

/** Which of the two states the one card is serving. */
export const ASSIGNMENT_KIND = {
  /** A live placement: the person is working. The date is their next shift. */
  placement: 'placement',
  /**
   * A submission: they applied and are waiting.
   *
   * The date is their FIRST DAY, and the card frames the whole thing as *what you applied for* — because
   * telling someone who has not been placed that they have an assignment is a promise the kiosk cannot keep.
   */
  submission: 'submission',
} as const;
export type AssignmentKind = (typeof ASSIGNMENT_KIND)[keyof typeof ASSIGNMENT_KIND];

export interface AssociateStatus {
  readonly kind: AssignmentKind;
  readonly clientName: string;
  /**
   * Site AND street address, both — KAS-1's title says so in as many words.
   *
   * A site name alone is useless to somebody who has to physically get there; an address alone does not
   * match what a recruiter said to them on the phone. The frame draws ONE combined `Location` row with only
   * the street address, which is a divergence raised for the designer rather than resolved here.
   */
  readonly siteName: string;
  readonly streetAddress: string;
  readonly shift: string;
  /**
   * The one date. Its LABEL follows `kind`; the value does not change meaning.
   *
   * Decision 0071 retired next-payday from this card, and the frame agrees — it draws Client, Location,
   * Shift and First day, and no pay field of any kind. (KAS-1's warning that the frame "predates 0071 and
   * still draws next-shift/next-payday" is out of date and is reported with this change.)
   *
   * A pre-formatted string rather than a timestamp: the api owns the format, so the kiosk cannot drift from
   * what a recruiter reads on their own screen, and a device with a skewed clock cannot render a wrong day.
   */
  readonly date: string;
  /**
   * The same day as `date`, as an ISO calendar date (`YYYY-MM-DD`, UTC), or `''` when there is none
   * [ISS-144]. The card formats it in the kiosk's language, because `date` is en-US and read
   * *"Primer día Tue, Aug 5"* on a Spanish kiosk. Optional: an api that predates it sends only `date`, and
   * the card falls back to that.
   */
  readonly dateValue?: string;
}
